"use client";

import {
  ArrowDown,
  ArrowUpRight,
  Pause,
  Play,
  Volume2,
  VolumeX,
} from "lucide-react";
import { MotionConfig, motion, useScroll, useSpring } from "motion/react";
import Image from "next/image";
import Link from "next/link";
import { type ReactNode, useEffect, useRef, useState } from "react";

import type { StoryRecord, TaggedProduct } from "../../_types/content";
import FeaturedProductCarousel from "../storefront/FeaturedProductCarousel";
import FeaturedStoryCarousel from "../storefront/FeaturedStoryCarousel";
import { fondamento, neueHeitz } from "../../global-fonts";
import type {
  ProjectDashboardAudioBlock,
  ProjectDashboardBlock,
  ProjectDashboardContentPreview,
  ProjectDashboardData,
  ProjectDashboardImage,
} from "./types";

const REVEAL_VIEWPORT = { once: true, amount: 0.15 } as const;
const REVEAL_TRANSITION = {
  duration: 1.55,
  ease: [0.22, 1, 0.36, 1],
} as const;
const IMAGE_TRANSITION = {
  duration: 2.2,
  ease: [0.22, 1, 0.36, 1],
} as const;
const IMAGE_BACKGROUND_CLASSES = {
  sand: "bg-sand",
  sky: "bg-sky",
  millet: "bg-millet",
  clay: "bg-clay",
  amaranth: "bg-amaranth",
  fire: "bg-fire",
  burgundy: "bg-burgundy",
  "young-grain": "bg-young-grain",
  "young-grain-darker": "bg-young-grain-darker",
} as const;

function Reveal({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 48, filter: "blur(8px)" }}
      whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      viewport={REVEAL_VIEWPORT}
      transition={REVEAL_TRANSITION}
      style={{ willChange: "transform, opacity, filter" }}
    >
      {children}
    </motion.div>
  );
}

function ContentHeading({
  title,
  compact = false,
}: {
  title: string;
  compact?: boolean;
}) {
  return (
    <Reveal
      className={`bg-sand px-6 text-center ${compact ? "py-10" : "py-20 sm:px-8 sm:py-28 lg:py-36"}`}
    >
      <h2
        className={`${neueHeitz.className} mx-auto max-w-[80rem] leading-tight tracking-[-0.025em] text-burgundy ${compact ? "text-3xl" : "text-4xl sm:text-6xl lg:text-7xl"}`}
      >
        {title}
      </h2>
    </Reveal>
  );
}

function FullBleedImage({
  image,
  heading,
  fit = "cover",
  background,
  compact = false,
}: {
  image: ProjectDashboardImage;
  heading?: string;
  fit?: "cover" | "contain";
  background?: keyof typeof IMAGE_BACKGROUND_CLASSES;
  compact?: boolean;
}) {
  const backgroundClass = background
    ? IMAGE_BACKGROUND_CLASSES[background]
    : fit === "contain"
      ? IMAGE_BACKGROUND_CLASSES.sand
      : "bg-earth";

  return (
    <>
      {heading ? <ContentHeading title={heading} compact={compact} /> : null}
      <Reveal
        className={`relative w-full ${compact ? "aspect-[4/5]" : "h-[72svh] min-h-[34rem] lg:h-[88svh]"} ${backgroundClass}`}
      >
        <motion.div
          className="absolute inset-0"
          initial={{ scale: 1.045 }}
          whileInView={{ scale: 1 }}
          viewport={REVEAL_VIEWPORT}
          transition={IMAGE_TRANSITION}
        >
          <Image
            src={image.src}
            alt={image.alt}
            fill
            sizes={compact ? "24rem" : "100vw"}
            className={fit === "contain" ? "object-contain" : "object-cover"}
          />
        </motion.div>
        {image.caption ? (
          <p
            className={`absolute right-5 bottom-5 max-w-xs text-right text-[0.65rem] leading-relaxed tracking-[0.14em] text-sand uppercase ${compact ? "" : "sm:right-8 sm:bottom-7"}`}
          >
            {image.caption}
          </p>
        ) : null}
      </Reveal>
    </>
  );
}

function TextBlock({
  heading,
  body,
  dropCap,
  hasDivider,
  compact = false,
}: {
  heading?: string;
  body: string;
  dropCap?: boolean;
  hasDivider?: boolean;
  compact?: boolean;
}) {
  return (
    <Reveal
      className={`w-full px-6 ${compact ? "py-10" : "py-16 sm:py-24 lg:py-32"}`}
    >
      {hasDivider ? <BlockDivider compact={compact} /> : null}
      {heading ? (
        <h2
          className={`${neueHeitz.className} mx-auto w-full max-w-[100rem] text-center text-3xl leading-tight tracking-[-0.02em] text-burgundy ${compact ? "" : "sm:text-5xl"}`}
        >
          {heading}
        </h2>
      ) : null}
      <p
        className={`project-dashboard-body mx-auto max-w-[43rem] whitespace-pre-line text-lg leading-[1.85] text-earth ${compact ? "" : "sm:text-xl"} ${heading ? "mt-12" : ""} ${dropCap ? "project-dashboard-drop-cap" : ""}`}
      >
        {body}
      </p>
    </Reveal>
  );
}

function BlockDivider({
  compact = false,
  flush = false,
}: {
  compact?: boolean;
  flush?: boolean;
}) {
  return (
    <div
      className={`mx-auto w-full max-w-6xl border-t border-burgundy/20 ${flush ? "" : compact ? "-mt-10 mb-10" : "-mt-16 mb-16 sm:-mt-24 sm:mb-24 lg:-mt-32 lg:mb-32"}`}
      aria-hidden="true"
    />
  );
}

function QuoteBlock({
  body,
  attribution,
  hasDivider,
  compact = false,
}: {
  body: string;
  attribution?: string;
  hasDivider?: boolean;
  compact?: boolean;
}) {
  return (
    <Reveal
      className={`bg-young-grain-darker px-6 text-center text-sand ${compact ? "py-14" : "py-28 sm:py-40 lg:py-52"}`}
    >
      {hasDivider ? <BlockDivider compact={compact} /> : null}
      <blockquote>
        <p
          className={`${fondamento.className} mx-auto max-w-[16ch] leading-[1.2] text-millet ${compact ? "text-3xl" : "text-4xl sm:text-6xl lg:text-7xl"}`}
        >
          “{body}”
        </p>
        {attribution ? (
          <footer className="mt-8 text-xs font-bold tracking-[0.2em] text-sand uppercase">
            — {attribution}
          </footer>
        ) : null}
      </blockquote>
    </Reveal>
  );
}

function formatAudioTime(seconds: number) {
  if (!Number.isFinite(seconds)) {
    return "0:00";
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}

function ProjectAudioPlayer({
  block,
  compact = false,
}: {
  block: ProjectDashboardAudioBlock;
  compact?: boolean;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio) {
      return;
    }
    const audioElement = audio;

    function syncDuration() {
      if (Number.isFinite(audioElement.duration) && audioElement.duration > 0) {
        setDuration(audioElement.duration);
      }
    }

    syncDuration();
    audioElement.addEventListener("loadedmetadata", syncDuration);
    audioElement.addEventListener("durationchange", syncDuration);
    audioElement.addEventListener("canplay", syncDuration);

    return () => {
      audioElement.removeEventListener("loadedmetadata", syncDuration);
      audioElement.removeEventListener("durationchange", syncDuration);
      audioElement.removeEventListener("canplay", syncDuration);
    };
  }, [block.src]);

  function togglePlayback() {
    const audio = audioRef.current;

    if (!audio) {
      return;
    }

    if (audio.paused) {
      audio.play().catch(() => setIsPlaying(false));
    } else {
      audio.pause();
    }
  }

  function updateVolume(nextVolume: number) {
    const audio = audioRef.current;

    if (!audio) {
      return;
    }

    audio.volume = nextVolume;
    audio.muted = nextVolume === 0;
    setVolume(nextVolume);
    setIsMuted(nextVolume === 0);
  }

  function toggleMute() {
    const audio = audioRef.current;

    if (!audio) {
      return;
    }

    audio.muted = !audio.muted;
    setIsMuted(audio.muted);
  }

  return (
    <div
      className={`mt-10 flex w-full items-center gap-3 rounded-xl bg-burgundy px-4 py-3 text-sand ${compact ? "" : "sm:gap-4 sm:px-5"}`}
      role="group"
      aria-label={block.label}
    >
      <audio
        ref={audioRef}
        preload="metadata"
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        className="sr-only"
      >
        <source src={block.src} />
      </audio>

      <button
        type="button"
        onClick={togglePlayback}
        className="grid size-9 shrink-0 place-items-center text-sand/75 transition-colors hover:text-sand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sand"
        aria-label={isPlaying ? "Pause audio" : "Play audio"}
      >
        {isPlaying ? <Pause size={20} aria-hidden="true" /> : <Play size={20} aria-hidden="true" />}
      </button>

      <input
        type="range"
        min="0"
        max={duration || 1}
        step="0.1"
        value={Math.min(currentTime, duration || 1)}
        onInput={(event) => {
          const nextTime = Number(event.currentTarget.value);
          if (audioRef.current) {
            audioRef.current.currentTime = nextTime;
          }
          setCurrentTime(nextTime);
        }}
        className="project-dashboard-range min-w-0 flex-1 cursor-pointer"
        aria-label="Audio progress"
      />

      <p className="shrink-0 text-xs font-medium text-sand/70 tabular-nums">
        {formatAudioTime(currentTime)} / {formatAudioTime(duration)}
      </p>

      <button
        type="button"
        onClick={toggleMute}
        className="grid size-9 shrink-0 place-items-center text-sand/75 transition-colors hover:text-sand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sand"
        aria-label={isMuted ? "Unmute audio" : "Mute audio"}
      >
        {isMuted ? <VolumeX size={20} aria-hidden="true" /> : <Volume2 size={20} aria-hidden="true" />}
      </button>

      <input
        type="range"
        min="0"
        max="1"
        step="0.05"
        value={isMuted ? 0 : volume}
        onChange={(event) => updateVolume(Number(event.currentTarget.value))}
        className={`project-dashboard-range hidden w-20 cursor-pointer ${compact ? "" : "sm:block"}`}
        aria-label="Audio volume"
      />
    </div>
  );
}

function AudioBlock({
  block,
  hasDivider,
  compact = false,
}: {
  block: ProjectDashboardAudioBlock;
  hasDivider?: boolean;
  compact?: boolean;
}) {
  return (
    <section
      className={`bg-sky px-6 text-earth ${compact ? "py-14" : "py-28 sm:px-8 sm:py-36 lg:py-44"}`}
    >
      {hasDivider ? <BlockDivider compact={compact} /> : null}
      <Reveal className="mx-auto max-w-5xl">
        {block.eyebrow ? (
          <p className="mb-7 text-xs font-bold tracking-[0.25em] text-burgundy uppercase">
            {block.eyebrow}
          </p>
        ) : null}
        <h2
          className={`${neueHeitz.className} w-full text-balance leading-[0.95] tracking-[-0.035em] text-earth ${compact ? "text-3xl" : "text-5xl sm:text-7xl"}`}
        >
          {block.title}
        </h2>
        {block.description ? (
          <p
            className={`${fondamento.className} mt-8 max-w-2xl text-2xl leading-relaxed text-burgundy ${compact ? "" : "sm:text-3xl"}`}
          >
            {block.description}
          </p>
        ) : null}
        <ProjectAudioPlayer block={block} compact={compact} />
        {block.transcriptUrl ? (
          <Link
            href={block.transcriptUrl}
            className="mt-5 inline-block text-sm font-bold underline underline-offset-4"
          >
            Read transcript
          </Link>
        ) : null}
      </Reveal>
    </section>
  );
}

function ContentCard({
  content,
  compact = false,
}: {
  content: ProjectDashboardContentPreview;
  compact?: boolean;
}) {
  const linkProps = content.external
    ? { target: "_blank", rel: "noopener noreferrer" }
    : {};

  return (
    <article className="group flex h-full flex-col border border-earth/20 bg-sand">
      {content.image ? (
        <Link
          href={content.href}
          aria-label={`View ${content.title}`}
          className="relative block aspect-[4/3] overflow-hidden bg-clay"
          {...linkProps}
        >
          <Image
            src={content.image.src}
            alt={content.image.alt}
            fill
            sizes={
              compact
                ? "24rem"
                : "(min-width: 1024px) 30vw, (min-width: 640px) 48vw, 100vw"
            }
            className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </Link>
      ) : null}
      <div className={`flex flex-1 flex-col p-6 ${compact ? "" : "sm:p-8"}`}>
        <p className="text-xs font-bold tracking-[0.2em] text-clay uppercase">
          {content.context ?? content.type}
        </p>
        <h3
          className={`${neueHeitz.className} mt-4 text-3xl leading-tight text-burgundy`}
        >
          <Link href={content.href} {...linkProps}>
            {content.title}
          </Link>
        </h3>
        <p className="mt-5 text-lg leading-relaxed text-earth">{content.summary}</p>
        <Link
          href={content.href}
          className="mt-auto pt-8 text-sm font-bold tracking-[0.16em] text-burgundy uppercase underline-offset-4 group-hover:underline"
          {...linkProps}
        >
          {content.type === "story"
            ? "Read story"
            : content.type === "recipe"
              ? "View recipe"
              : "View product"}
          {content.external ? (
            <ArrowUpRight className="ml-2 inline" size={16} aria-hidden="true" />
          ) : null}
        </Link>
      </div>
    </article>
  );
}

function ContentBlock({
  heading,
  description,
  content,
  compact = false,
}: {
  heading?: string;
  description?: string;
  content: ProjectDashboardContentPreview[];
  compact?: boolean;
}) {
  return (
    <section
      className={`bg-sand px-6 ${compact ? "py-12" : "py-24 sm:px-8 sm:py-32 lg:py-40"}`}
    >
      <Reveal className="mx-auto max-w-6xl">
        {heading ? (
          <h2
            className={`${neueHeitz.className} max-w-[14ch] leading-tight text-burgundy ${compact ? "text-3xl" : "text-4xl sm:text-6xl"}`}
          >
            {heading}
          </h2>
        ) : null}
        {description ? (
          <p className="mt-7 max-w-2xl text-xl leading-relaxed text-earth">
            {description}
          </p>
        ) : null}
        <div
          className={`mt-12 grid gap-8 ${compact ? "" : "md:grid-cols-2 lg:grid-cols-3"}`}
        >
          {content.map((item) => (
            <ContentCard
              key={`${item.type}:${item.id}`}
              content={item}
              compact={compact}
            />
          ))}
        </div>
      </Reveal>
    </section>
  );
}

function ProjectBlockView({
  block,
  hasDivider,
  compact = false,
  hideHeading = false,
}: {
  block: ProjectDashboardBlock;
  hasDivider?: boolean;
  compact?: boolean;
  hideHeading?: boolean;
}) {
  switch (block.type) {
    case "text":
      return (
        <TextBlock
          heading={hideHeading ? undefined : block.heading}
          body={block.body}
          dropCap={block.dropCap}
          hasDivider={hasDivider}
          compact={compact}
        />
      );
    case "heading":
      return hideHeading ? null : (
        <ContentHeading title={block.title} compact={compact} />
      );
    case "image":
      return (
        <FullBleedImage
          image={block.image}
          heading={hideHeading ? undefined : block.heading}
          fit={block.fit}
          background={block.background}
          compact={compact}
        />
      );
    case "quote":
      return (
        <QuoteBlock
          body={block.body}
          attribution={block.attribution}
          hasDivider={hasDivider}
          compact={compact}
        />
      );
    case "audio":
      return (
        <AudioBlock
          block={block}
          hasDivider={hasDivider}
          compact={compact}
        />
      );
    case "content":
      return (
        <ContentBlock
          heading={hideHeading ? undefined : block.heading}
          content={[block.content]}
          compact={compact}
        />
      );
    case "content-collection":
      return (
        <ContentBlock
          heading={hideHeading ? undefined : block.heading}
          description={block.description}
          content={block.content}
          compact={compact}
        />
      );
  }
}

function ProjectDashboardBlockList({
  blocks,
  compact = false,
  headingToOmit,
}: {
  blocks: ProjectDashboardBlock[];
  compact?: boolean;
  headingToOmit?: string;
}) {
  return blocks.map((block, index) => {
    const blockHeading =
      block.type === "heading"
        ? block.title
        : "heading" in block
          ? block.heading
          : undefined;

    return (
      <ProjectBlockView
        key={block.id}
        block={block}
        compact={compact}
        hideHeading={Boolean(headingToOmit && blockHeading === headingToOmit)}
        hasDivider={
          (block.type === "text" ||
            block.type === "quote" ||
            block.type === "audio") &&
          blocks[index - 1]?.type === block.type
        }
      />
    );
  });
}

export function ProjectDashboardBlocks({
  blocks,
  compact = false,
  headingToOmit,
}: {
  blocks: ProjectDashboardBlock[];
  compact?: boolean;
  headingToOmit?: string;
}) {
  return (
    <MotionConfig reducedMotion="user">
      <article className="project-dashboard-content bg-sand text-earth">
        <ProjectDashboardBlockList
          blocks={blocks}
          compact={compact}
          headingToOmit={headingToOmit}
        />
      </article>
    </MotionConfig>
  );
}

export function ProjectDashboardFeaturedContent({
  featuredStories = [],
  featuredProducts = [],
  compact = false,
}: {
  featuredStories?: readonly StoryRecord[];
  featuredProducts?: readonly TaggedProduct[];
  compact?: boolean;
}) {
  return (
    <>
      {featuredStories.length > 0 ? (
        <section aria-label="Featured Stories" className="bg-sand">
          <div className={compact ? "px-6" : "px-6 sm:px-8"}>
            <BlockDivider flush />
          </div>
          <ContentHeading title="Featured Stories" compact={compact} />
          <div className={compact ? "px-2 pb-10" : "px-6 pb-20 sm:px-8 sm:pb-28"}>
            <FeaturedStoryCarousel records={featuredStories} compact={compact} />
          </div>
        </section>
      ) : null}

      {featuredProducts.length > 0 ? (
        <section aria-label="Featured Products" className="bg-sand">
          <div className={compact ? "px-6" : "px-6 sm:px-8"}>
            <BlockDivider flush />
          </div>
          <ContentHeading title="Featured Products" compact={compact} />
          <div className={compact ? "px-2 pb-10" : "px-6 pb-20 sm:px-8 sm:pb-28"}>
            <FeaturedProductCarousel products={featuredProducts} compact={compact} />
          </div>
        </section>
      ) : null}
    </>
  );
}

export default function ProjectDashboardTemplate({
  project,
  children,
  featuredStories = [],
  featuredProducts = [],
}: {
  project: ProjectDashboardData;
  children?: ReactNode;
  featuredStories?: readonly StoryRecord[];
  featuredProducts?: readonly TaggedProduct[];
}) {
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 80, damping: 25 });

  return (
    <MotionConfig reducedMotion="user">
      <main className="project-dashboard-page bg-sand text-earth">
        <style jsx global>{`
          body:has(.project-dashboard-page) > header,
          body:has(.project-dashboard-page) > footer {
            display: none;
          }

          body:has(.project-dashboard-page) {
            background: var(--sand);
          }

        `}</style>

        <motion.div
          className="fixed top-0 left-0 z-[100] h-[2px] w-full origin-left bg-burgundy"
          style={{ scaleX: progress }}
          aria-hidden="true"
        />

        <header className="sticky top-0 z-50 grid h-14 grid-cols-3 items-center border-b border-sand/15 bg-burgundy px-5 text-sand sm:px-8">
          <Link
            href="/"
            className="text-[0.65rem] font-bold tracking-[0.18em] uppercase"
          >
            RG
          </Link>
          <p
            className={`${neueHeitz.className} text-center text-sm tracking-wide sm:text-base`}
          >
            {project.headerTitle}
          </p>
          <p className="text-right text-[0.6rem] font-bold tracking-[0.16em] uppercase">
            {project.headerLabel}
          </p>
        </header>

        <section className="flex min-h-[calc(100svh-3.5rem)] flex-col items-center justify-center px-2 py-24 text-center sm:px-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.6 }}
            className="flex w-full flex-col items-center"
          >
            {project.eyebrow ? (
              <p className="text-xs font-bold tracking-[0.26em] text-clay uppercase">
                {project.eyebrow}
              </p>
            ) : null}
            <h1
              className={`${neueHeitz.className} mt-10 w-full text-[clamp(3.5rem,8.5vw,9rem)] leading-[0.9] tracking-[-0.045em] text-burgundy`}
            >
              <span className="mx-auto block w-full max-w-[140rem]">
                {project.title}
              </span>
              {project.accentTitle ? (
                <span
                  className={`${fondamento.className} mx-auto block w-full max-w-[130rem] pt-8 text-[0.85em] text-clay`}
                >
                  {project.accentTitle}
                </span>
              ) : null}
            </h1>
            <p
              className="mx-auto mt-12 w-full max-w-[100rem] text-lg leading-relaxed sm:text-2xl"
            >
              {project.summary}
            </p>
            {project.blocks.length > 0 ? (
              <ArrowDown
                className="mt-14 text-clay"
                size={30}
                strokeWidth={1.25}
                aria-hidden="true"
              />
            ) : null}
          </motion.div>
        </section>

        <article>
          {children}
          <ProjectDashboardBlockList blocks={project.blocks} />
        </article>

        <ProjectDashboardFeaturedContent
          featuredStories={featuredStories}
          featuredProducts={featuredProducts}
        />

        {project.footer ? (
          <footer className="border-t border-sand/20 bg-burgundy px-6 py-20 text-sand sm:px-8 lg:py-28">
            {project.footer.type === "citations" ? (
              <Reveal className="mx-auto max-w-5xl">
                <h2
                  className={`${neueHeitz.className} text-4xl text-sand sm:text-6xl`}
                >
                  {project.footer.title}
                </h2>
                <ol className="mt-10 list-decimal space-y-5 pl-6 text-lg leading-relaxed text-sand sm:text-xl">
                  {project.footer.items.map((item) => (
                    <li key={item} className="pl-2">
                      {item}
                    </li>
                  ))}
                </ol>
              </Reveal>
            ) : (
              <Reveal className="mx-auto flex max-w-5xl flex-col items-center text-center">
                <p className="text-[0.65rem] font-bold tracking-[0.25em] text-millet uppercase">
                  {project.footer.eyebrow}
                </p>
                <h2
                  className={`${neueHeitz.className} mt-7 text-4xl text-sand sm:text-6xl`}
                >
                  {project.footer.title}
                </h2>
                <Link
                  href={project.footer.href}
                  className="group mt-10 flex items-center gap-3 border-b border-millet pb-2 text-xs font-bold tracking-[0.18em] text-millet uppercase"
                >
                  {project.footer.linkLabel}
                  <ArrowUpRight
                    size={16}
                    className="transition-transform group-hover:-translate-y-1 group-hover:translate-x-1"
                    aria-hidden="true"
                  />
                </Link>
              </Reveal>
            )}
          </footer>
        ) : null}
      </main>
    </MotionConfig>
  );
}
